/* eslint-disable react/prop-types */
import { useState } from 'react';
// material-ui
import {
  Button,
  Card,
  CardMedia,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import { useNavigate } from 'react-router-dom';

// assets
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

import { EmailIcon } from 'material-icons';

import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import Twitter from '@mui/icons-material/Twitter';

import FacebookIcon from 'assets/images/footer/icon-facebook.png';
import TwitterIcon from 'assets/images/footer/icon-twitter.png';

import RichTextEditor from 'components/RichText';

import { formatEventDate, formatEventTime } from 'utils/formatDateTime';

import { SponsorshipProps } from '../pages/EventSponsorship';

import { REACT_APP_CLOUDFRONT_URL } from 'config/config';
import { SponsorshipIcon } from 'material-icons';
import CheckoutForm from '../modals/Checkout';

const ShareButton = ({
  title,
  icon,
  onClick,
}: {
  title: string;
  icon: any;
  onClick: () => void;
}) => {
  return (
    <Tooltip title={title} placement="top">
      <IconButton onClick={onClick} size="small">
        <img
          src={icon}
          alt={title}
          style={{
            backgroundColor: '#673ab7',
            borderRadius: 24,
            border: '2px solid #673ab7',
          }}
        />
      </IconButton>
    </Tooltip>
  );
};

function EventSponsorship({
  eventSponsorship,
}: {
  eventSponsorship: SponsorshipProps;
}) {
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [showCheckout, setShowCheckout] = useState(false);

  const imageUrl = eventSponsorship.event.imageUrl || 'EventHiDefault.jpeg';

  const endsAtFormatted = new Intl.DateTimeFormat('en-US', {
    timeZone: eventSponsorship.event.timezone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'shortGeneric',
  }).format(new Date(eventSponsorship.event.endsAt));

  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=https://www.eventhi.io/event/${eventSponsorship.nameSlug}&amp;src=sdkpreparse`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=%27https://www.eventhi.io/event/${eventSponsorship.nameSlug}%27`;
  const googleMapsUrl = `https://maps.google.com/maps?q=${eventSponsorship.event.eventLocation?.streetAddress.replaceAll(
    ' ',
    '+'
  )}+${eventSponsorship.event.eventLocation?.city.replaceAll(' ', '+')}+${
    eventSponsorship.event.eventLocation?.state
  }+${eventSponsorship.event.eventLocation?.postalCode}}`;

  const handleShare = (type: string) => {
    switch (type) {
      case 'facebook':
        window.open(facebookShareUrl, '_blank');
        break;
      case 'twitter':
        window.open(twitterShareUrl, '_blank');
        break;
    }
  };

  const shareLinks = eventSponsorship.event.allowShare
    ? [
        {
          title: 'Share to Facebook',
          icon: FacebookIcon,
          onClick: () => handleShare('facebook'),
        },
        {
          title: 'Share to Twitter',
          icon: TwitterIcon,
          onClick: () => handleShare('twitter'),
        },
      ]
    : [];

  return (
    <Container sx={{ margin: 'auto' }}>
      {/* Image */}
      <Grid item xs={12}>
        <Card sx={{ m: '0 auto', border: 'none' }}>
          <CardMedia
            component="img"
            image={`${REACT_APP_CLOUDFRONT_URL}/${imageUrl}`}
            sx={{
              objectFit: 'contain',
              position: 'relative',
              top: 0,
              left: 0,
              height: 'inherit',
              width: '100%',
            }}
          />
        </Card>
      </Grid>
      <Grid container spacing={1}>
        {/* Event Title */}
        <Grid item xs={12}>
          <Stack alignItems="center" justifyContent="space-between">
            {eventSponsorship.event.status === 'Canceled' && (
              <Typography
                fontFamily="Montserrat"
                fontWeight="bold"
                variant="h1"
                sx={{ pt: 2 }}
              >
                THIS EVENT HAS BEEN CANCELED
              </Typography>
            )}
            <Typography
              fontFamily="Montserrat"
              fontWeight="bold"
              variant="h2"
              sx={{ pt: 2 }}
            >
              {eventSponsorship.event.name}
            </Typography>
            <Grid container justifyContent="flex-end">
              {shareLinks.map((link) => (
                <ShareButton key={link.title} {...link} />
              ))}
            </Grid>
            <Button
              fullWidth
              color="secondary"
              variant="contained"
              startIcon={<SponsorshipIcon />}
              onClick={() => setShowCheckout(true)}
              sx={{ mt: 2 }}
              disabled={eventSponsorship.event.status === 'Canceled'}
            >
              Get Sponsorships
            </Button>
            <Button
              fullWidth
              color="secondary"
              variant="outlined"
              startIcon={<EventOutlinedIcon />}
              onClick={() =>
                navigate(`/events/${eventSponsorship.event.nameSlug}`)
              }
              sx={{ mt: 2 }}
            >
              View Event page
            </Button>
          </Stack>
        </Grid>
        {/* Description & Time/Place */}
        <Grid item xs={12}>
          <Grid container direction={matchDownMD ? 'column-reverse' : 'row'}>
            {/* Description 10 */}
            <Grid item xs={12} md={9}>
              {RichTextEditor && (
                <RichTextEditor
                  eventId={eventSponsorship.id}
                  value={eventSponsorship.description}
                  readOnly={true}
                />
              )}
              <Grid
                container
                justifyContent="center"
                flexDirection="column"
                sx={{ mt: 4 }}
              >
                <Typography variant="caption" textAlign="center">
                  Hosted By
                </Typography>
                <Typography variant="h4" textAlign="center" fontWeight="400">
                  {eventSponsorship.event.hostname}
                </Typography>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <IconButton
                      LinkComponent="a"
                      target="_blank"
                      href={`mailto:${eventSponsorship.event.contactEmail}`}
                      type="link"
                    >
                      <EmailIcon fontSize="medium" />
                    </IconButton>
                    {eventSponsorship.event.twitterLink && (
                      <IconButton
                        sx={{ color: 'black' }}
                        onClick={() =>
                          window.open(
                            eventSponsorship.event.twitterLink,
                            '_blank'
                          )
                        }
                      >
                        <Twitter />
                      </IconButton>
                    )}
                    {eventSponsorship.event.instagramLink && (
                      <IconButton
                        onClick={() =>
                          window.open(
                            eventSponsorship.event.instagramLink,
                            '_blank'
                          )
                        }
                      >
                        <Instagram />
                      </IconButton>
                    )}
                    {eventSponsorship.event.facebookLink && (
                      <IconButton
                        onClick={() =>
                          window.open(
                            eventSponsorship.event.facebookLink,
                            '_blank'
                          )
                        }
                      >
                        <Facebook />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
            {/* Time/Place 2 */}
            <Grid item xs={12} md={3}>
              <Grid pb={3} pt={2}>
                <Grid container flexDirection="row">
                  <EventOutlinedIcon sx={{ fontSize: '48px' }} />
                  <Typography variant="h4" mt={3}>
                    When
                  </Typography>
                </Grid>
                <Divider />
                <Grid item>
                  <ul>
                    <li>
                      {formatEventDate(
                        eventSponsorship.event.startsAt,
                        eventSponsorship.event.timezone
                      )}
                    </li>
                    <li>
                      {formatEventTime(
                        eventSponsorship.event.startsAt,
                        eventSponsorship.event.timezone
                      )}
                      {' to '}
                      {endsAtFormatted}
                    </li>
                  </ul>
                </Grid>
              </Grid>
              <Grid pb={3}>
                <Grid container flexDirection="row">
                  <PlaceOutlinedIcon sx={{ fontSize: '48px' }} />
                  <Typography variant="h4" mt={3}>
                    Where
                  </Typography>
                </Grid>
                <Divider />
                <Grid item pl={3}>
                  {eventSponsorship.event.isVirtual && (
                    <Typography variant="h6">
                      This event is virtual. Join from anywhere!
                    </Typography>
                  )}
                  {!eventSponsorship.event.isVirtual &&
                    eventSponsorship.event.eventLocation &&
                    !eventSponsorship.event.hideLocation && (
                      <Grid
                        item
                        sx={{
                          pt: 1,
                          ':hover': { color: 'lightBlue', cursor: 'pointer' },
                        }}
                        onClick={() => window.open(googleMapsUrl, '_blank')}
                      >
                        {eventSponsorship.event.eventLocation.name} <br />
                        {eventSponsorship.event.eventLocation.formattedAddress}
                      </Grid>
                    )}
                  {!eventSponsorship.event.isVirtual &&
                    eventSponsorship.event.eventLocation &&
                    eventSponsorship.event.hideLocation && (
                      <Typography variant="h6" sx={{ pt: 1 }}>
                        {eventSponsorship.event.eventLocation.city}
                        {', '}
                        {eventSponsorship.event.eventLocation.state}
                      </Typography>
                    )}
                </Grid>
              </Grid>
              <Grid pb={3}>
                <Grid container flexDirection="row">
                  <LightbulbOutlinedIcon sx={{ fontSize: '48px' }} />
                  <Typography variant="h4" mt={3}>
                    Details
                  </Typography>
                </Grid>
                <Divider />
                <Grid item>
                  {eventSponsorship.event.id === 9408 ? (
                    <ul>
                      <li>
                        <Typography variant="h6">
                          {eventSponsorship.event.refundPolicy > 0
                            ? `Refunds accepted ${eventSponsorship.event.refundPolicy} day(s) before event`
                            : 'This event does not accept refunds'}
                        </Typography>
                      </li>
                    </ul>
                  ) : (
                    <ul>
                      <li>
                        <Typography
                          variant="h6"
                          color={
                            eventSponsorship.event.cannabisConsumption
                              ? 'success'
                              : 'error'
                          }
                          sx={{ pt: 1 }}
                        >
                          {eventSponsorship.event.cannabisConsumption
                            ? 'Cannabis consumption is allowed at this event'
                            : 'No cannabis is allowed at this event'}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="h6">
                          {eventSponsorship.event.private
                            ? 'This is a private, invite-only event'
                            : 'This event is open to the public'}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="h6">
                          {eventSponsorship.event.refundPolicy > 0
                            ? `Refunds accepted ${eventSponsorship.event.refundPolicy} day(s) before event`
                            : 'This event does not accept refunds'}
                        </Typography>
                      </li>
                    </ul>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <CheckoutForm
        isOpen={showCheckout}
        eventId={eventSponsorship.eventId}
        setIsOpen={() => setShowCheckout(!showCheckout)}
        type="sponsorship"
      />
    </Container>
  );
}

export default EventSponsorship;
